import AsyncDataTable from "../core/async-data-table/async-data-table";
import { DatableColumnType } from "../core/async-data-table/type";
import { endpoints } from "@/api/endpoints/endpoints";
import FilterHeader from "../core/async-data-table/filter-header";
import { Button } from "@/components/ui/button";
import { Plus, Minus, TrashIcon } from "lucide-react";
import { useMemo } from "react";
import { formatCurrency } from "@/components/orders/order-items-utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { Chip } from "@mui/material";

interface VariantAsyncTableProps {
  onAdd?: (row: any) => void;
  cartItems?: Array<{ variantId: string; quantity: number }>;
  onUpdateQty?: (variantId: string, quantity: number) => void;
  onRemove?: (variantId: string) => void;
}

const VariantAsyncTable = ({ 
  onAdd, 
  cartItems = [], 
  onUpdateQty, 
  onRemove,
}: VariantAsyncTableProps) => {
  const isMobile = useIsMobile();
  
  // Helper function to get cart item
  const getCartItem = (rowId: string) => 
    cartItems.find((item) => String(item.variantId) === String(rowId));
  
  // Helper function to handle quantity changes
  const handleQuantityChange = (row: any, delta: number) => {
    const cartItem = getCartItem(String(row.id));
    const currentQty = cartItem?.quantity || 0;
    const newQty = currentQty + delta;
    
    if (newQty <= 0) {
      onRemove?.(String(row.id));
    } else if (cartItem && onUpdateQty) {
      onUpdateQty(String(row.id), newQty);
    } else if (delta > 0 && onAdd) {
      onAdd(row);
    }
  };
  
  const cols: DatableColumnType[] = useMemo(() => {
    const columns: DatableColumnType[] = [];
    
    // SKU Column (left-aligned)
    columns.push({
      name: 'sku',
      id: 'pv.sku',
      label: 'SKU',
      searchable: true,
      orderable: true,
      datatype: 'string' as const,
      align: 'left' as const,
    });

    // Stock Column (left-aligned or center)
    columns.push({
      name: 'stockInHand',
      id: 'pv.stock_in_hand',
      label: 'Stock',
      searchable: false,
      orderable: false,
      datatype: 'string' as const,
      align: 'left' as const,
      Render: ({ row }: { row: any }) => (
        <Chip label={row.stockInHand} variant="outlined" color="success" />
      ),
    });
  
    
    // Price Column (right-aligned)
    columns.push({
      name: 'price',
      id: 'pv.price',
      label: 'Price',
      searchable: false,
      orderable: true,
      datatype: 'string' as const,
      align: 'right' as const,
      Render: ({ row }: { row: any }) => {
        return <span className="text-right text-sm font-medium block">{formatCurrency(row.price)}</span>;
      },
    });
    
    // Actions Column (right-aligned)
    columns.push({
      name: 'actions',
      id: 'actions',
      label: isMobile ? '' : 'Actions',
      searchable: false,
      orderable: false,
      datatype: 'custom' as const,
      align: 'right' as const,
      Render: ({ row }: { row: any }) => {
        const cartItem = getCartItem(String(row.id));
        const currentQty = cartItem?.quantity || 0;
        const isInCart = currentQty > 0;
        
        if (isInCart) {
          // Show quantity controls when item is in cart
          return (
            <div className="flex items-center justify-end gap-1">
              <Button
                size="icon"
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  handleQuantityChange(row, -1);
                }}
                className="h-8 w-8"
                aria-label="Decrease quantity"
              >
                <Minus className="h-3.5 w-3.5" />
              </Button>
              
              <span className="text-sm font-semibold min-w-[2.5rem] text-center px-2">
                {currentQty}
              </span>
              
              <Button
                size="icon"
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  handleQuantityChange(row, 1);
                }}
                className="h-8 w-8"
                aria-label="Increase quantity"
              >
                <Plus className="h-3.5 w-3.5" />
              </Button>
              
              <Button
                size="icon"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove?.(String(row.id));
                }}
                className="h-8 w-8 ml-1 text-destructive hover:bg-destructive/10 hover:text-destructive"
                aria-label="Remove from cart"
              >
                <TrashIcon className="h-3.5 w-3.5" />
              </Button>
            </div>
          );
        }
        
        // Show Add button when item is not in cart
        return (
          <div className="flex justify-end">
            <Button
              size={isMobile ? "icon" : "sm"}
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                onAdd?.(row);
              }}
              className={isMobile ? "h-8 w-8" : "h-8 px-4"}
              aria-label="Add Item"
            >
              {isMobile ? (
                <Plus className="h-4 w-4" />
              ) : (
                <>
                  <Plus className="h-3.5 w-3.5 mr-1.5" />
                  Add
                </>
              )}
            </Button>
          </div>
        );
      },
    });
    
    // Line Total Column (desktop only, right-aligned)
    if (!isMobile) {
      columns.push({
        name: 'lineTotal',
        id: 'lineTotal',
        label: 'Total',
        searchable: false,
        orderable: false,
        datatype: 'custom' as const,
        align: 'right' as const,
        Render: ({ row }: { row: any }) => {
          const cartItem = getCartItem(String(row.id));
          if (!cartItem) return <span className="text-sm text-muted-foreground">-</span>;
          
          const price = parseFloat(row.price || 0);
          const lineTotal = price * cartItem.quantity;
          
          return (
            <span className="text-sm font-semibold tabular-nums block text-right">
              {formatCurrency(lineTotal)}
            </span>
          );
        },
      });
    }
    
    return columns;
  }, [isMobile, cartItems, onAdd, onUpdateQty, onRemove]);

  return (
    <AsyncDataTable
      dataUrl={endpoints.variant.list}
      columns={cols}
      autoIndex={!isMobile}
      FilterComponent={FilterHeader}
      refreshPage={false}
      orderByDef="pv.created_at"
      orderDef="desc"
    />
  );
};

export default VariantAsyncTable;